import React, { useState } from "react";
import { IconMessage, IconMail, IconWebhook, IconEye, IconCheck } from "@tabler/icons-react";
import "./TemplateSelector.css";

const TemplateSelector = ({ 
  templateType, 
  availableTemplates = [], 
  selectedTemplate, 
  onTemplateSelect,
  loading = false 
}) => {
  const [showPreview, setShowPreview] = useState(null);

  const getTypeConfig = (type) => {
    const configs = {
      whatsapp: {
        title: "Templates WhatsApp",
        icon: IconMessage,
        color: "#25D366",
        gradient: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)"
      },
      email: {
        title: "Templates de E-mail",
        icon: IconMail,
        color: "#3b82f6",
        gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
      },
      webhook: {
        title: "Templates Webhook",
        icon: IconWebhook,
        color: "#8b5cf6",
        gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
      }
    };
    return configs[type] || configs.whatsapp;
  };

  const typeConfig = getTypeConfig(templateType);
  const IconComponent = typeConfig.icon;

  const handleTemplateSelect = (template) => {
    onTemplateSelect(template);
  };

  const handlePreviewToggle = (templateId) => {
    setShowPreview(showPreview === templateId ? null : templateId);
  };

  const renderTemplatePreview = (template) => {
    switch (templateType) {
      case 'whatsapp':
        return (
          <div className="template-selector__whatsapp-preview">
            <div className="template-selector__whatsapp-message">
              <div className="template-selector__message-bubble">
                {template?.use_ai_generation ? "Mensagem será gerada com IA a partir do histórico da conversa." : template.message?.text || 'Mensagem não definida'}
              </div>
              {template.media_url && (
                <div className="template-selector__media-info">
                  📎 {template.media_type} anexado
                </div>
              )}
            </div>
          </div>
        );
      
      case 'email':
        return (
          <div className="template-selector__email-preview">
            <div className="template-selector__email-header">
              <div className="template-selector__email-subject">
                {template.email_subject || 'Sem assunto definido'}
              </div>
              <div className="template-selector__email-from">
                De: {template.email_from || template.email_display_name || 'Não definido'}
              </div>
            </div>
           
          </div>
        );
      
      case 'webhook':
        return (
          <div className="template-selector__webhook-preview">
            <div className="template-selector__webhook-info">
              <div className="template-selector__info-item">
                <span className="template-selector__info-label">Endpoint:</span>
                <span className="template-selector__info-value">{template.webhook_url || 'Não configurado'}</span>
              </div>
              <div className="template-selector__info-item">
                <span className="template-selector__info-label">Método:</span>
                <span className="template-selector__info-value">{template.webhook_method || 'POST'}</span>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="template-selector">
        <div className="template-selector__loading-state">
          <div className="template-selector__loading-spinner"></div>
          <p className="template-selector__loading-text">Carregando templates...</p>
        </div>
      </div>
    );
  }

  if (!availableTemplates.length) {
    return (
      <div className="template-selector">
        <div className="template-selector__empty-state">
          <div className="template-selector__empty-icon">
            <IconComponent size={48} />
          </div>
          <h4 className="template-selector__empty-title">Nenhum template encontrado</h4>
          <p className="template-selector__empty-description">
            Não há templates de {templateType} disponíveis.
            <br />
            Crie templates primeiro no módulo de Templates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="template-selector">
      <div className="template-selector__header">
        <div className={`template-selector__header-icon template-selector__header-icon--${templateType}`}>
          <IconComponent size={20} color="white" />
        </div>
        <div className="template-selector__header-info">
          <h4 className="template-selector__header-title">{typeConfig.title}</h4>
          <p className="template-selector__header-subtitle">
            {availableTemplates.length} template{availableTemplates.length !== 1 ? 's' : ''} disponível{availableTemplates.length !== 1 ? 'eis' : ''}
          </p>
        </div>
      </div>

      <div className="template-selector__grid">
        {availableTemplates.map((template) => {
          const isSelected = selectedTemplate?.id === template.id;
          const isPreviewOpen = showPreview === template.id;

          return (
            <div 
              key={template.id}
              className={`template-selector__card template-selector__card--${templateType} ${isSelected ? 'template-selector__card--selected' : ''}`}
              style={isSelected ? { '--selected-color': typeConfig.color } : {}}
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="template-selector__card-header">
                <h5 className="template-selector__card-title">{template.name}</h5>
                <div className="template-selector__card-actions">
                  <button
                    className={`template-selector__preview-button ${isPreviewOpen ? 'template-selector__preview-button--active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviewToggle(template.id);
                    }}
                  >
                    <IconEye size={16} />
                  </button>
                  {isSelected && (
                    <div className={`template-selector__selected-badge template-selector__selected-badge--${templateType}`}>
                      <IconCheck size={14} />
                    </div>
                  )}
                </div>
              </div>

              <div className="template-selector__card-content">
                <div className="template-selector__card-meta">
                  {templateType === 'email' && template.email_subject && (
                    <div className="template-selector__meta-item">
                      <span className="template-selector__meta-label">Assunto:</span>
                      <span className="template-selector__meta-value">
                        {template.email_subject.length > 50 
                          ? `${template.email_subject.substring(0, 50)}...`
                          : template.email_subject
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {isPreviewOpen && (
                <div className="template-selector__preview-container">
                  <div className="template-selector__preview-header">
                    <h6 className="template-selector__preview-title">Preview</h6>
                  </div>
                  {renderTemplatePreview(template)}
                </div>
              )}

              {isSelected && (
                <div className={`template-selector__selected-indicator template-selector__selected-indicator--${templateType}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


export default TemplateSelector;